import React, { Fragment, ReactNode } from 'react';

import theme from 'theme';

import { CustomCampaignSteps } from '../CustomCampaign';
import { CampaignStateTypes } from '../customCampaignReducer';

import CustomCampaignNavigationBlock from './CustomCampaignNavigationBlock';
import { buildNavForest, NavItem, NavSend } from './navTree';

type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    onRemoveClick: (index: number) => void;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

// Subtle-bordered box that groups a conditional split's two branches. The green accent is
// expressed via the per-branch left lines below, not the box border itself.
const splitBoxStyles = {
    border: `1.5px solid ${theme.palette.primary.main}26`,
    borderRadius: '8px',
    padding: '8px 8px 8px 0',
    marginTop: '4px',
    marginBottom: '8px',
    backgroundColor: 'rgba(0, 132, 91, 0.02)',
};

// Each branch (meets / does-not-meet) gets a green left accent line matching Option A.
const branchGroupStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
};

const accentLineStyles: React.CSSProperties = {
    width: '3px',
    background: theme.palette.primary.main,
    borderRadius: '2px',
    margin: '4px 10px 4px 8px',
    flexShrink: 0,
};

const branchContentStyles: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
};

const CustomCampaignNavigation = ({
    activeSendIndex,
    activeStep,
    campaignState,
    onRemoveClick,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const forest = buildNavForest(campaignState.sends);
    const rendered = new Set<number>();

    const renderBlock = (index: number): ReactNode => {
        rendered.add(index);
        return (
            <CustomCampaignNavigationBlock
                activeSendIndex={activeSendIndex}
                activeStep={activeStep}
                campaignState={campaignState}
                key={`block-${index}`}
                index={index}
                onRemoveClick={onRemoveClick}
                send={campaignState.sends[index]}
                setActiveSendIndex={setActiveSendIndex}
                setActiveStep={setActiveStep}
            />
        );
    };

    const renderSend = (node: NavSend): ReactNode => (
        <Fragment key={`send-${node.index}`}>
            {renderBlock(node.index)}
            {node.children.map(renderItem)}
        </Fragment>
    );

    const renderItem = (item: NavItem): ReactNode => {
        if (item.kind === 'split') {
            return (
                <div style={splitBoxStyles} key={`split-${item.meets.index}`}>
                    <div style={branchGroupStyles}>
                        <div style={accentLineStyles} />
                        <div style={branchContentStyles}>{renderSend(item.meets)}</div>
                    </div>
                    <div style={branchGroupStyles}>
                        <div style={accentLineStyles} />
                        <div style={branchContentStyles}>{renderSend(item.doesNotMeet)}</div>
                    </div>
                </div>
            );
        }
        return renderSend(item);
    };

    const tree = forest.map(renderItem);

    // Defensive: surface any sends the tree did not reach (e.g. malformed or partially-built
    // drafts) so a send is never silently hidden from the navigation.
    const leftovers = campaignState.sends
        .map((_, index) => index)
        .filter((index) => !rendered.has(index))
        .map((index) => renderBlock(index));

    return (
        <>
            {tree}
            {leftovers}
        </>
    );
};

export default CustomCampaignNavigation;
